/* =============================================================================
   dom - the DOM utilities shared by the core, the mixins and the components.

   What it offers: querying (`qs` / `qsa`), element creation (`create`), design-
   token reads (`cssVar` / `cssVarNum`), page-relative geometry (`pageRect`) and
   floating-panel placement (`placePanel`).
   Rules: nothing here touches jQuery - the facade is the only jQuery-aware layer
   of this library, so these helpers stay usable anywhere. Every token read is
   guarded, so importing this module where there is no DOM is safe. `cssVar` is
   used internally by `cssVarNum`; keep both.
   Exports: qs · qsa · create · cssVar · cssVarNum · pageRect · placePanel.
   No dependencies.
   ============================================================================= */

/** querySelector shorthand */
export const qs = (sel, root = document) => root.querySelector(sel);
/** querySelectorAll -> real array */
export const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));

/** create an element with attributes + optional text */
export function create(tag, attrs = {}, text) {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === 'class') el.className = v;
    else if (k === 'style' && typeof v === 'object') Object.assign(el.style, v);
    else if (v !== undefined && v !== null) el.setAttribute(k, v);
  }
  if (text !== undefined) el.textContent = text;
  return el;
}

/** read a CSS custom property (design token) off :root or an element;
    returns '' where there is no DOM, so importing this module is always safe */
export function cssVar(name, el) {
  if (typeof getComputedStyle === 'undefined' || typeof document === 'undefined') return '';
  return getComputedStyle(el || document.documentElement).getPropertyValue(name).trim();
}

/** numeric css token (e.g. --ics-z-dropdown -> 5000) */
export function cssVarNum(name, el) {
  const v = parseFloat(cssVar(name, el));
  return isNaN(v) ? 0 : v;
}

/** viewport-relative rect (getBoundingClientRect + page scroll) */
export function pageRect(el) {
  const r = el.getBoundingClientRect();
  return {
    top: r.top + window.scrollY, left: r.left + window.scrollX,
    bottom: r.bottom + window.scrollY, right: r.right + window.scrollX,
    width: r.width, height: r.height,
  };
}

/**
 * Position a floating panel under (or over, when space is short) an anchor.
 * Used by PopupMixin for calendar, combo and autocomplete panels. The panel is
 * absolutely positioned in page coordinates, so it must be appended to <body>;
 * it is also clamped to stay inside the viewport horizontally.
 * Returns the placement actually used ('below' | 'above').
 */
export function placePanel(panel, anchor, { gap = 2, align = 'left' } = {}) {
  const a = pageRect(anchor);
  const panelH = panel.offsetHeight, panelW = panel.offsetWidth;
  const spaceBelow = window.scrollY + window.innerHeight - a.bottom;
  const below = spaceBelow >= panelH || spaceBelow >= a.top - window.scrollY;
  panel.style.position = 'absolute';
  panel.style.top = (below ? a.bottom + gap : a.top - panelH - gap) + 'px';
  let left = align === 'right' ? a.right - panelW : a.left;
  left = Math.max(4, Math.min(left, window.scrollX + document.documentElement.clientWidth - panelW - 4));
  panel.style.left = left + 'px';
  return below ? 'below' : 'above';
}
